// Phase 2.12 (Edit Mode B) — on-page widget editing for the portfolio
// dashboard. Mirrors the project dashboard's `dashboard-grid` pattern
// but autosaves on every change instead of a "Done" handoff: the
// surface is small (≤12 widgets in practice), the save is cheap (one
// JSON column write), and skipping the explicit save step means the
// user can navigate away mid-edit without losing the layout.
//
// What gets saved: the per-user `user_settings.portfolio_dashboard_layout`
// JSON column, read by `CompaniesController#resolve_portfolio_template`
// between the URL `?template=` override and the company default.
// "Save as new template" hands the same widget list to the existing
// `dashboard-layout-templates` modal, so promoting a personal layout
// to a shared template is a single click.

import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

const DEBOUNCE_MS = 500

export default class extends Controller {
  static targets = ["grid", "widget", "banner", "customizeButton", "addPopover", "addPopoverList"]
  static values = {
    saveUrl: String,
    resetUrl: String,
    csrfToken: String,
    availableWidgets: Array
  }

  connect() {
    this._editMode = false
    this._sortable = null
    this._saveTimeout = null
  }

  disconnect() {
    this._teardown()
  }

  // ── Edit mode lifecycle ──────────────────────────────────────────

  toggle(event) {
    event?.preventDefault()
    if (this._editMode) {
      this._exit()
    } else {
      this._enter()
    }
  }

  _enter() {
    this._editMode = true
    this.element.classList.add("portfolio-edit-mode")
    this.gridTarget.classList.add("ring-2", "ring-brand", "ring-offset-2", "rounded-lg", "p-2", "-m-2")

    if (this.hasBannerTarget) this.bannerTarget.classList.remove("hidden")
    if (this.hasCustomizeButtonTarget) {
      this.customizeButtonTarget.classList.add("bg-brand", "text-white", "hover:bg-brand-dark")
      this.customizeButtonTarget.classList.remove("bg-gray-200", "hover:bg-gray-300", "text-gray-600")
      const label = this.customizeButtonTarget.querySelector("[data-customize-label]")
      if (label) label.textContent = "Done"
    }

    this.widgetTargets.forEach((w) => this._showWidgetChrome(w))
    this._renderAddPopoverList()

    this._sortable = Sortable.create(this.gridTarget, {
      handle: "[data-portfolio-edit-mode-drag-handle]",
      animation: 180,
      ghostClass: "opacity-30",
      forceFallback: true,
      onEnd: () => this._scheduleSave()
    })
  }

  _exit() {
    this._editMode = false
    this.element.classList.remove("portfolio-edit-mode")
    this.gridTarget.classList.remove("ring-2", "ring-brand", "ring-offset-2", "rounded-lg", "p-2", "-m-2")

    if (this.hasBannerTarget) this.bannerTarget.classList.add("hidden")
    if (this.hasCustomizeButtonTarget) {
      this.customizeButtonTarget.classList.remove("bg-brand", "text-white", "hover:bg-brand-dark")
      this.customizeButtonTarget.classList.add("bg-gray-200", "hover:bg-gray-300", "text-gray-600")
      const label = this.customizeButtonTarget.querySelector("[data-customize-label]")
      if (label) label.textContent = "Customize"
    }

    this.widgetTargets.forEach((w) => this._hideWidgetChrome(w))
    this._closeAddPopover()
    this._teardownSortable()

    // Flush any pending autosave so we don't drop the last edit.
    if (this._saveTimeout) {
      clearTimeout(this._saveTimeout)
      this._saveTimeout = null
      this._persist()
    }
  }

  _teardown() {
    this._teardownSortable()
    if (this._saveTimeout) {
      clearTimeout(this._saveTimeout)
      this._saveTimeout = null
    }
  }

  _teardownSortable() {
    if (this._sortable) {
      this._sortable.destroy()
      this._sortable = null
    }
  }

  // ── Widget actions ───────────────────────────────────────────────

  removeWidget(event) {
    event?.preventDefault()
    const widget = event.currentTarget.closest("[data-portfolio-edit-mode-target='widget']")
    if (!widget) return
    widget.remove()
    this._renderAddPopoverList()
    this._scheduleSave()
  }

  cycleColSpan(event) {
    event?.preventDefault()
    const btn = event.currentTarget
    const widget = btn.closest("[data-portfolio-edit-mode-target='widget']")
    if (!widget) return

    const next = parseInt(btn.dataset.colSpanValue, 10) || 1
    this._applyColSpan(widget, next)
    this._refreshColSpanButtons(widget, next)
    this._scheduleSave()
  }

  // ── Add widget popover ───────────────────────────────────────────

  toggleAddPopover(event) {
    event?.preventDefault()
    if (!this.hasAddPopoverTarget) return
    if (this.addPopoverTarget.classList.contains("hidden")) {
      this._renderAddPopoverList()
      this.addPopoverTarget.classList.remove("hidden")
    } else {
      this._closeAddPopover()
    }
  }

  closeAddPopoverOnBackdrop(event) {
    if (event.target === event.currentTarget) this._closeAddPopover()
  }

  _closeAddPopover() {
    if (this.hasAddPopoverTarget) this.addPopoverTarget.classList.add("hidden")
  }

  addWidget(event) {
    event?.preventDefault()
    const key = event.currentTarget.dataset.widgetKey
    if (!key) return

    const widget = this._buildPlaceholderWidget(key)
    this.gridTarget.appendChild(widget)
    this._closeAddPopover()
    this._scheduleSave()
  }

  _buildPlaceholderWidget(key) {
    const meta = (this.availableWidgetsValue || []).find((w) => w.key === key) || { title: key }

    const wrapper = document.createElement("div")
    wrapper.className = "relative bg-white border border-gray-200 rounded-lg overflow-hidden"
    wrapper.setAttribute("data-portfolio-edit-mode-target", "widget")
    wrapper.setAttribute("data-widget-type", key)
    wrapper.setAttribute("data-widget-col-span", "1")
    wrapper.setAttribute("data-widget-settings", "{}")
    wrapper.innerHTML = `
      <div class="px-4 py-8 flex flex-col items-center justify-center text-center text-gray-400">
        <p class="text-[13px]">${this._escape(meta.title || key)}</p>
        <p class="text-[11px] mt-1">Will load on save</p>
      </div>
    `
    this._showWidgetChrome(wrapper)
    return wrapper
  }

  // ── Reset / save-as-template ─────────────────────────────────────

  async reset(event) {
    event?.preventDefault()
    if (!confirm("Reset to the default layout? Your customizations will be cleared.")) return

    try {
      const resp = await fetch(this.resetUrlValue, {
        method: "DELETE",
        headers: this._jsonHeaders(),
        credentials: "same-origin"
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      window.location.reload()
    } catch (err) {
      console.warn("[portfolio-edit-mode] reset failed", err)
      alert("Reset failed. Try again.")
    }
  }

  // The Save-as-Template button piggybacks on the existing layout-
  // templates modal, which reads `data-portfolio-widgets` from the
  // grid (already kept fresh by `_writeManifest`). Dispatching a
  // CustomEvent the templates controller listens for keeps the two
  // controllers loosely coupled.
  saveAsTemplate(event) {
    event?.preventDefault()
    this._writeManifest()
    document.dispatchEvent(new CustomEvent("portfolio-edit-mode:save-as-template", { bubbles: true }))
  }

  // ── Widget chrome (drag / remove / col-span) ─────────────────────

  _showWidgetChrome(widget) {
    if (widget.querySelector("[data-portfolio-edit-mode-chrome]")) return

    const colSpan = parseInt(widget.dataset.widgetColSpan, 10) || 1
    const chrome = document.createElement("div")
    chrome.setAttribute("data-portfolio-edit-mode-chrome", "")
    // Normal-flow bar above the widget. Self-wrapped portfolio-scope
    // partials carry their own card chrome, so an absolute-positioned
    // overlay would either cover their title or require per-widget
    // padding hacks. Sitting above keeps view-mode markup untouched.
    chrome.className = "flex items-center justify-between px-2 py-1 mb-1.5 rounded-md bg-white border border-gray-200 text-[11px] text-gray-500 shadow-sm"
    chrome.innerHTML = `
      <button type="button"
              data-portfolio-edit-mode-drag-handle
              class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-gray-100 cursor-move"
              title="Drag to reorder">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"/></svg>
        <span class="hidden sm:inline">Drag</span>
      </button>
      <div class="inline-flex items-center gap-1">
        ${this._renderColSpanToggle(colSpan)}
        <button type="button"
                data-action="click->portfolio-edit-mode#removeWidget"
                class="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                title="Remove">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
        </button>
      </div>
    `
    widget.prepend(chrome)
  }

  _hideWidgetChrome(widget) {
    const chrome = widget.querySelector("[data-portfolio-edit-mode-chrome]")
    if (chrome) chrome.remove()
  }

  _renderColSpanToggle(current) {
    return [1, 2, 3].map((n) => {
      const active = n === current
      return `<button type="button"
                      data-action="click->portfolio-edit-mode#cycleColSpan"
                      data-col-span-value="${n}"
                      class="px-1.5 py-0.5 rounded text-[11px] font-medium ${active ? 'bg-brand text-white' : 'text-gray-500 hover:bg-gray-100'}"
                      title="Set width to ${n} column${n === 1 ? '' : 's'}">${n}</button>`
    }).join("")
  }

  _refreshColSpanButtons(widget, current) {
    const chrome = widget.querySelector("[data-portfolio-edit-mode-chrome]")
    if (!chrome) return
    chrome.querySelectorAll("[data-col-span-value]").forEach((btn) => {
      const n = parseInt(btn.dataset.colSpanValue, 10)
      const active = n === current
      btn.classList.toggle("bg-brand", active)
      btn.classList.toggle("text-white", active)
      btn.classList.toggle("text-gray-500", !active)
      btn.classList.toggle("hover:bg-gray-100", !active)
    })
  }

  _applyColSpan(widget, span) {
    widget.dataset.widgetColSpan = String(span)
    widget.classList.remove("lg:col-span-1", "lg:col-span-2", "lg:col-span-3")
    if (span === 2) widget.classList.add("lg:col-span-2")
    if (span === 3) widget.classList.add("lg:col-span-3")
  }

  // ── Add-widget popover render ────────────────────────────────────

  _renderAddPopoverList() {
    if (!this.hasAddPopoverListTarget) return

    const present = new Set(this.widgetTargets.map((w) => w.dataset.widgetType))
    const available = (this.availableWidgetsValue || []).filter((w) => !present.has(w.key))

    if (!available.length) {
      this.addPopoverListTarget.innerHTML = `<p class="text-[12px] text-gray-400 px-3 py-2">All widgets are already on your dashboard.</p>`
      return
    }

    this.addPopoverListTarget.innerHTML = available.map((w) => `
      <button type="button"
              data-action="click->portfolio-edit-mode#addWidget"
              data-widget-key="${this._escape(w.key)}"
              class="w-full text-left px-3 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50 cursor-pointer">
        <span class="block font-medium">${this._escape(w.title)}</span>
        ${w.description ? `<span class="block text-[11px] text-gray-500">${this._escape(w.description)}</span>` : ""}
      </button>
    `).join("")
  }

  // ── Persistence ──────────────────────────────────────────────────

  _scheduleSave() {
    this._writeManifest()
    if (this._saveTimeout) clearTimeout(this._saveTimeout)
    this._saveTimeout = setTimeout(() => {
      this._saveTimeout = null
      this._persist()
    }, DEBOUNCE_MS)
  }

  // Keep `data-portfolio-widgets` (read by the templates modal's
  // `_collectCurrentWidgets` fallback) in lock-step with the live grid
  // so `Save as new template` always sees the current layout.
  _writeManifest() {
    const widgets = this._collectWidgets()
    this.gridTarget.dataset.portfolioWidgets = JSON.stringify(widgets)
  }

  async _persist() {
    const widgets = this._collectWidgets()
    if (!widgets.length) return

    try {
      const resp = await fetch(this.saveUrlValue, {
        method: "PATCH",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ widgets })
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
    } catch (err) {
      console.warn("[portfolio-edit-mode] save failed", err)
    }
  }

  _collectWidgets() {
    return this.widgetTargets.map((el, idx) => ({
      widget_type: el.dataset.widgetType,
      col_span: parseInt(el.dataset.widgetColSpan, 10) || 1,
      position: idx,
      settings: this._parseJSON(el.dataset.widgetSettings) || {}
    })).filter((w) => Boolean(w.widget_type))
  }

  // ── Utils ────────────────────────────────────────────────────────

  _jsonHeaders() {
    return {
      "Accept": "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": this.csrfTokenValue || (document.querySelector('meta[name="csrf-token"]')?.content || "")
    }
  }

  _parseJSON(raw) {
    if (!raw) return null
    try { return JSON.parse(raw) } catch { return null }
  }

  _escape(str) {
    return String(str || "").replace(/[&<>"']/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }
}
